import { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  ChevronRight,
  FileJson,
  GitBranch,
  Loader2,
  RotateCcw,
  ScrollText,
  XCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { OrchestrationHierarchyTree } from '@/components/learningTutorial/OrchestrationHierarchyTree';
import { TimelineBlocksOutline } from '@/components/learningTutorial/TimelineBlocksOutline';
import { NarrationSyncOutline } from '@/components/learningTutorial/NarrationSyncOutline';
import {
  TUTORIAL_PIPELINE_STEPS,
  buildExtendedPassLog,
  isStepComplete,
  pipelineStepStatus,
} from '@/lib/learningTutorial/pipelineSteps';
import { needsAudioPipelineRun, resumeStepMeta } from '@/lib/learningTutorial/pipelineRunner';
import { suggestedArtifactGapsForProject } from '@/lib/learningTutorial/programmingContext';
import { buildOrchestrationViewerTabs } from '@/lib/learningTutorial/orchestrationViewerTabs';
import { TimelineScriptSourcePanel } from '@/components/learningTutorial/TimelineScriptSourcePanel';
import { TimelineArtifactPlanPanel } from '@/components/learningTutorial/TimelineArtifactPlanPanel';

function formatJson(value) {
  if (value == null) return 'null';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function StatusIcon({ state }) {
  if (state === 'done') return <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />;
  if (state === 'active') return <Loader2 className="w-4 h-4 text-amber-400 animate-spin shrink-0" />;
  if (state === 'error') return <XCircle className="w-4 h-4 text-red-400 shrink-0" />;
  return <GitBranch className="w-4 h-4 text-slate-600 shrink-0" />;
}

function StepOutputPanel({
  step,
  orchestration,
  project,
  viewMode,
  pipelineError,
  resumeStepId,
  onRetryStep,
  onRetryAndContinue,
  onRunStep,
  resumeLoading,
  onNavigateToStep,
}) {
  const showRetry = resumeStepId === step.id
    && !isStepComplete(step.id, project, orchestration)
    && (onRetryStep || onRetryAndContinue);
  const errorText = pipelineError || project?.last_pipeline_error;
  const history = orchestration?.pass_history || {};
  const intake = project?.intake || {};
  const suggestedGaps = step.id === 'pass2' && orchestration?.pipeline_pass >= 2
    ? suggestedArtifactGapsForProject(project)
    : [];

  function SuggestedGapsBanner() {
    if (!suggestedGaps.length) return null;
    const includeCode = intake.include_code === true;
    return (
      <div className="rounded-sm border border-amber-500/30 bg-amber-950/25 px-3 py-2 text-xs text-amber-200/90 space-y-1 mb-3">
        <p className="font-medium text-amber-300">Suggested artifacts skipped (optional)</p>
        <p className="text-amber-200/80">
          Pass 2 did not select recommended code or dry-run artifacts for {suggestedGaps.length} concept(s).
          Re-run Pass 2{includeCode ? '' : ' with Include code enabled'} — selection stays LLM-driven, not guaranteed.
        </p>
        <ul className="text-[11px] text-amber-200/70 list-disc pl-4 max-h-24 overflow-y-auto">
          {suggestedGaps.slice(0, 6).map((row) => (
            <li key={row.concept_path}>
              {row.concept_title}: {row.suggested_missing?.join(' · ')}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (step.kind === 'orchestration') {
    const scriptPayload = {
      script: history.pass3?.script || orchestration?.script,
      topic: orchestration?.topic || history.pass3?.topic,
      derived: history.pass3?.derived || orchestration?.derived,
    };
    const data = step.tabId === 'script'
      ? scriptPayload
      : history[step.tabId] || (step.tabId === 'pass3' ? history.pass3 : null)
        || (step.id === 'pass1' && history.pass1)
        || (step.id === 'pass2' && history.pass2)
        || (step.id === 'pass2_optimize' && history.pass2_optimize)
        || orchestration;

    const hasScriptTree = Boolean(
      data?.script?.scenes?.[0]?.topic_tree?.length
      || data?.scenes?.[0]?.topic_tree?.length,
    );
    if (step.id === 'script' && !hasScriptTree) {
      return <p className="text-sm text-slate-500">Waiting for {step.shortLabel}…</p>;
    }
    if (!data?.concept_tree?.length && step.id !== 'script') {
      return <p className="text-sm text-slate-500">Waiting for {step.shortLabel}…</p>;
    }
    if (step.id === 'pass2_optimize' && data?.optimization_note) {
      return (
        <div className="space-y-3">
          <p className="text-sm text-emerald-300/90">{data.optimization_note}</p>
          {viewMode === 'json' ? (
            <pre className="text-xs leading-relaxed text-emerald-100/90 font-mono whitespace-pre-wrap">
              {formatJson(data)}
            </pre>
          ) : (
            <>
              <SuggestedGapsBanner />
              <OrchestrationHierarchyTree tabId="pass2" data={data} intake={intake} />
            </>
          )}
        </div>
      );
    }

    if (viewMode === 'json') {
      return (
        <pre className="text-xs leading-relaxed text-emerald-100/90 font-mono whitespace-pre-wrap">
          {formatJson(data)}
        </pre>
      );
    }

    return (
      <>
        {step.id === 'pass2' ? <SuggestedGapsBanner /> : null}
        <OrchestrationHierarchyTree tabId={step.tabId} data={data} intake={intake} />
      </>
    );
  }

  if (step.kind === 'timeline') {
    if (!project?.timeline?.blocks?.length) {
      return <p className="text-sm text-slate-500">Timeline not generated yet.</p>;
    }
    const scriptPayload = project?.script
      || orchestration?.pass_history?.pass3?.script
      || orchestration?.script;
    if (viewMode === 'json') {
      return (
        <div className="space-y-4">
          <TimelineScriptSourcePanel
            timeline={project.timeline}
            script={scriptPayload}
            orchestration={orchestration}
            viewMode="json"
            onOpenScriptStep={onNavigateToStep ? () => onNavigateToStep('script') : undefined}
          />
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Timeline JSON</p>
            <pre className="text-xs leading-relaxed text-emerald-100/90 font-mono whitespace-pre-wrap">
              {formatJson(project.timeline)}
            </pre>
          </div>
          {scriptPayload ? (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Trainer script JSON (project.script)</p>
              <pre className="text-xs leading-relaxed text-emerald-100/90 font-mono whitespace-pre-wrap">
                {formatJson(scriptPayload)}
              </pre>
            </div>
          ) : null}
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <TimelineScriptSourcePanel
          timeline={project.timeline}
          script={scriptPayload}
          orchestration={orchestration}
          viewMode="outline"
          onOpenScriptStep={onNavigateToStep ? () => onNavigateToStep('script') : undefined}
        />
        <TimelineArtifactPlanPanel timeline={project.timeline} />
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Timeline blocks (presentation)</p>
          <TimelineBlocksOutline
            timeline={project.timeline}
            visualSkips={project?.visual_generation_skips}
          />
        </div>
      </div>
    );
  }

  if (step.kind === 'narration_audio') {
    const clips = project?.narration_clip_assets || [];
    const needsRerun = clips.length === 0 && Boolean(project?.script);
    if (!project?.narration_audio_url && !needsRerun) {
      return <p className="text-sm text-slate-500">Trainer narration audio not generated yet.</p>;
    }
    const beatClips = clips.filter((c) => c?.beat_id).length;
    const elementClips = clips.filter((c) => c?.element_id && !c?.beat_id).length;
    const clipLabel = beatClips
      ? `${clips.length} clip(s) (${beatClips} beat-level)`
      : `${clips.length} clip(s)`;
    return (
      <div className="space-y-2 text-sm text-slate-300">
        <p>
          {clips.length > 0
            ? `Audio ready — ${clipLabel} stored for WhisperX alignment.`
            : 'Full audio URL exists but no per-clip assets — re-run narration audio after the beat-sync fix.'}
        </p>
        {elementClips > 0 && beatClips > 0 ? (
          <p className="text-xs text-slate-500">{elementClips} element clip(s) · {beatClips} beat clip(s)</p>
        ) : null}
        <p className="text-xs text-slate-500">Phase: {project?.narration_sync_phase || 'audio'}</p>
        {needsRerun && onRunStep ? (
          <div className="pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 rounded-sm border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              disabled={resumeLoading}
              onClick={() => onRunStep('narration_audio')}
              data-testid="outline-rerun-narration-audio"
            >
              {resumeLoading ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
              )}
              Re-run TTS (generate clips)
            </Button>
          </div>
        ) : null}
        {viewMode === 'json' ? (
          <pre className="text-xs font-mono text-emerald-100/90 whitespace-pre-wrap">
            {formatJson({
              narration_audio_url: project.narration_audio_url,
              narration_timings: project.narration_timings,
              narration_clip_assets: clips,
            })}
          </pre>
        ) : null}
      </div>
    );
  }

  if (step.kind === 'word_align') {
    const words = project?.rendered_timeline?.narration_word_timings;
    if (!words?.length) {
      return <p className="text-sm text-slate-500">WhisperX word alignment not run yet.</p>;
    }
    return (
      <div className="space-y-2 text-sm text-slate-300">
        <p>
          {project.rendered_timeline.word_timing_count || 0} words ·{' '}
          {project.rendered_timeline.screenplay_event_count || 0} screenplay events
        </p>
        {viewMode === 'json' ? (
          <pre className="text-xs font-mono text-emerald-100/90 whitespace-pre-wrap">
            {formatJson({
              alignment_mode: project.rendered_timeline.alignment_mode,
              narration_word_timings: words,
              screenplay_events: project.rendered_timeline.screenplay_events,
            })}
          </pre>
        ) : (
          <NarrationSyncOutline
            rendered={project?.rendered_timeline}
            narrationTimings={project?.narration_timings || project?.rendered_timeline?.narration_timings}
          />
        )}
      </div>
    );
  }

  if (step.kind === 'artifact_embed') {
    const embedded = project?.rendered_timeline?.trainer_artifacts_embedded;
    const count = project?.rendered_timeline?.trainer_artifacts_embedded_count || 0;
    const words = project?.rendered_timeline?.word_timing_count || 0;
    const clips = (project?.narration_clip_assets || []).length;
    if (!embedded && project?.narration_sync_phase !== 'complete') {
      return (
        <div className="space-y-2 text-sm text-slate-500">
          <p>Trainer artifact placement not embedded yet.</p>
          <p className="text-xs text-slate-600">
            Audio clips: {clips} · WhisperX words: {words}
          </p>
          {needsAudioPipelineRun(project) && onRunStep && onRetryAndContinue ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 rounded-sm border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              disabled={resumeLoading}
              onClick={onRetryAndContinue}
              data-testid="outline-run-audio-pipeline"
            >
              {resumeLoading ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
              )}
              Run audio pipeline
            </Button>
          ) : null}
        </div>
      );
    }
    return (
      <div className="space-y-2 text-sm text-slate-300">
        <p className="text-emerald-300">{count} artifact placement(s) synced to narration timing.</p>
        {viewMode === 'json' ? (
          <pre className="text-xs font-mono text-emerald-100/90 whitespace-pre-wrap">
            {formatJson({
              trainer_artifacts_embedded: embedded,
              trainer_artifacts_embedded_count: count,
              narration_sync_phase: project?.narration_sync_phase,
            })}
          </pre>
        ) : (
          <NarrationSyncOutline
            rendered={project?.rendered_timeline}
            narrationTimings={project?.narration_timings || project?.rendered_timeline?.narration_timings}
          />
        )}
      </div>
    );
  }

  if (step.kind === 'narration') {
    const timings = project?.narration_timings || project?.rendered_timeline?.narration_timings;
    const visualSkips = project?.visual_generation_skips || project?.timeline?.visual_generation_skips || [];
    if (!timings?.length) {
      return (
        <div className="space-y-3 text-sm text-slate-500">
          <p>Narration sync not run yet.</p>
          {errorText && showRetry ? (
            <p className="text-xs text-red-300/90 rounded-sm border border-red-500/25 bg-red-500/10 px-2 py-1.5">
              {errorText}
              {/timeout/i.test(errorText) ? (
                <span className="block mt-1 text-red-200/80">
                  Long lessons need several minutes for TTS + word alignment. After this fix, retries can run up to 2 hours.
                </span>
              ) : null}
            </p>
          ) : null}
          {visualSkips.length > 0 ? (
            <p className="text-xs text-amber-300/90">
              {visualSkips.length} visual(s) were skipped during timeline — narration can still complete without them.
            </p>
          ) : null}
          {showRetry ? (
            <div className="flex flex-wrap gap-2 pt-1">
              {onRetryStep ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 rounded-sm border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                  disabled={resumeLoading}
                  onClick={onRetryStep}
                  data-testid="outline-retry-step"
                >
                  {resumeLoading ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Retry this step
                </Button>
              ) : null}
              {onRetryAndContinue ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 rounded-sm border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                  disabled={resumeLoading}
                  onClick={onRetryAndContinue}
                  data-testid="outline-retry-continue"
                >
                  Retry &amp; continue
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      );
    }
    if (viewMode === 'json') {
      return (
        <pre className="text-xs leading-relaxed text-emerald-100/90 font-mono whitespace-pre-wrap">
          {formatJson({ narration_timings: timings, total_duration: project?.rendered_timeline?.total_duration })}
        </pre>
      );
    }
    return (
      <div className="space-y-3">
        {visualSkips.length > 0 ? (
          <p className="text-xs text-amber-300/90 rounded-sm border border-amber-500/25 bg-amber-500/10 px-2 py-1.5">
            Note: {visualSkips.length} slide image(s) were skipped (quota/unavailable). Narration synced for all text blocks.
          </p>
        ) : null}
        <NarrationSyncOutline
          rendered={project?.rendered_timeline}
          narrationTimings={timings}
        />
      </div>
    );
  }

  if (step.kind === 'summary') {
    const log = buildExtendedPassLog(orchestration, project);
    return (
      <div className="space-y-3 text-sm text-slate-300">
        <p className="text-emerald-300 font-medium">Pipeline complete — all stages recorded.</p>
        <ul className="space-y-1.5">
          {log.map((entry) => (
            <li key={entry.pass} className="flex items-start gap-2 text-xs">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
              <span>{entry.message}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return null;
}

/**
 * Step-by-step pipeline outline — shows each level's output as the flow progresses.
 */
export function PipelineOutlineDialog({
  open,
  onOpenChange,
  currentStepId,
  pipelineStatus = 'idle',
  orchestration = null,
  project = null,
  initialStepId = null,
  onOpenEditor,
  resumeStepId = null,
  pipelineError = null,
  resumeLoading = false,
  onRetryStep,
  onRetryAndContinue,
  onRunStep,
}) {
  const [selectedStepId, setSelectedStepId] = useState('pass1');
  const [viewMode, setViewMode] = useState('outline');

  const steps = TUTORIAL_PIPELINE_STEPS;
  const selectedStep = steps.find((s) => s.id === selectedStepId) || steps[0];
  const viewerTabs = useMemo(() => buildOrchestrationViewerTabs(orchestration), [orchestration]);
  const passLog = useMemo(
    () => buildExtendedPassLog(orchestration, project),
    [orchestration, project],
  );

  useEffect(() => {
    if (!open) return;
    const target = initialStepId || currentStepId || 'pass1';
    if (steps.some((s) => s.id === target)) {
      setSelectedStepId(target);
    }
  }, [open, currentStepId, initialStepId, steps]);

  useEffect(() => {
    if (!open || pipelineStatus !== 'working' || !currentStepId) return;
    setSelectedStepId(currentStepId);
  }, [open, pipelineStatus, currentStepId]);

  const showJsonToggle = selectedStep.kind !== 'summary';
  const stepStatusCtx = {
    currentStepId,
    pipelineStatus,
    project,
    orchestration,
  };
  const selectedIncomplete = resumeStepId === selectedStep.id
    && !isStepComplete(selectedStep.id, project, orchestration);
  const showRetryFooter = selectedIncomplete && (onRetryStep || onRetryAndContinue);
  const resumeMeta = resumeStepId ? resumeStepMeta(resumeStepId) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[88vh] flex flex-col bg-black border border-amber-500/30 text-slate-100 p-0 gap-0"
        data-testid="pipeline-outline-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-amber-500/20 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ScrollText className="w-5 h-5 text-amber-400" />
            Pipeline outline — step by step
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm space-y-1">
            <span className="block">Each stage updates the same document. Select a step to inspect its outline output.</span>
            {project?.script ? (
              <span className="block text-xs text-slate-500">
                Audio clips: {(project.narration_clip_assets || []).length}
                {' · '}
                Words: {project.rendered_timeline?.word_timing_count || 0}
                {' · '}
                Artifacts synced: {project.rendered_timeline?.trainer_artifacts_embedded_count || 0}
                {needsAudioPipelineRun(project) ? (
                  <span className="text-amber-400/90"> — audio pipeline incomplete</span>
                ) : null}
              </span>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 flex-col md:flex-row">
          {/* Step list */}
          <aside className="md:w-56 shrink-0 border-b md:border-b-0 md:border-r border-amber-500/15 bg-slate-950/80 overflow-y-auto">
            <ol className="p-2 space-y-0.5">
              {steps.map((step) => {
                const state = pipelineStepStatus(step.id, stepStatusCtx);
                const selected = step.id === selectedStepId;
                const logEntry = passLog.find((e) => {
                  if (step.id === 'script') return e.pass === 'pass3';
                  if (step.id === 'pass2_optimize') return e.pass === 'pass2_optimize';
                  return e.pass === step.id || e.output_snapshot_key === step.tabId;
                });
                return (
                  <li key={step.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedStepId(step.id)}
                      className={cn(
                        'w-full text-left px-3 py-2.5 rounded-sm transition-colors flex items-start gap-2',
                        selected
                          ? 'bg-amber-500/15 border border-amber-500/35'
                          : 'hover:bg-white/5 border border-transparent',
                      )}
                      data-testid={`pipeline-step-${step.id}`}
                    >
                      <StatusIcon state={state} />
                      <div className="min-w-0 flex-1">
                        <p className={cn(
                          'text-xs font-semibold leading-tight',
                          selected ? 'text-amber-200' : 'text-slate-200',
                        )}
                        >
                          {step.shortLabel}
                        </p>
                        <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">{step.label}</p>
                        {logEntry?.stats ? (
                          <p className="text-[10px] text-slate-600 mt-1">
                            {logEntry.stats.concept_nodes != null ? `${logEntry.stats.concept_nodes} nodes` : null}
                            {logEntry.stats.artifact_count != null ? ` · ${logEntry.stats.artifact_count} artifacts` : null}
                            {logEntry.stats.block_count != null ? `${logEntry.stats.block_count} blocks` : null}
                            {logEntry.stats.narration_clips != null ? `${logEntry.stats.narration_clips} clips` : null}
                          </p>
                        ) : null}
                      </div>
                      <ChevronRight className={cn(
                        'w-3.5 h-3.5 shrink-0 mt-0.5',
                        selected ? 'text-amber-400' : 'text-slate-600',
                      )}
                      />
                    </button>
                  </li>
                );
              })}
            </ol>
          </aside>

          {/* Output panel */}
          <div className="flex-1 flex flex-col min-w-0 min-h-[280px]">
            <div className="px-4 py-3 border-b border-slate-800 flex flex-wrap items-center justify-between gap-2 shrink-0">
              <div>
                <p className="text-sm font-medium text-amber-100">{selectedStep.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{selectedStep.description}</p>
                {selectedStep.id === 'pass2' && orchestration?.pass2_progress ? (
                  <p className="text-xs text-amber-400/90 mt-1">
                    Pass 2 — {orchestration.pass2_progress.completed ?? 0}
                    /{orchestration.pass2_progress.total ?? '?'} concepts enriched
                    {orchestration.pass_status?.pass2 === 'in_progress' ? ' (in progress)' : ''}
                  </p>
                ) : null}
              </div>
              {showJsonToggle ? (
                <div className="inline-flex rounded-sm border border-slate-700 p-0.5 bg-slate-950">
                  <button
                    type="button"
                    onClick={() => setViewMode('outline')}
                    className={cn(
                      'px-2.5 py-1 text-xs rounded-sm flex items-center gap-1',
                      viewMode === 'outline' ? 'bg-amber-500/20 text-amber-200' : 'text-slate-400',
                    )}
                  >
                    <GitBranch className="w-3 h-3" />
                    Outline
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('json')}
                    className={cn(
                      'px-2.5 py-1 text-xs rounded-sm flex items-center gap-1',
                      viewMode === 'json' ? 'bg-amber-500/20 text-amber-200' : 'text-slate-400',
                    )}
                  >
                    <FileJson className="w-3 h-3" />
                    JSON
                  </button>
                </div>
              ) : null}
            </div>

            <div className="flex-1 overflow-auto p-4 bg-slate-950/50">
              <StepOutputPanel
                step={selectedStep}
                orchestration={orchestration}
                project={project}
                viewMode={viewMode}
                pipelineError={pipelineError}
                resumeStepId={resumeStepId}
                onRetryStep={onRetryStep}
                onRetryAndContinue={onRetryAndContinue}
                onRunStep={onRunStep}
                resumeLoading={resumeLoading}
                onNavigateToStep={setSelectedStepId}
              />
            </div>

            {showRetryFooter && selectedStep.kind !== 'narration' && selectedStep.kind !== 'summary' ? (
              <div className="px-4 py-3 border-t border-slate-800 shrink-0 space-y-2">
                {(pipelineError || project?.last_pipeline_error) ? (
                  <p className="text-xs text-red-300/90 rounded-sm border border-red-500/25 bg-red-500/10 px-2 py-1.5">
                    {pipelineError || project?.last_pipeline_error}
                  </p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {onRetryStep ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 rounded-sm border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                      disabled={resumeLoading}
                      onClick={onRetryStep}
                    >
                      {resumeLoading ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      Retry {resumeMeta?.shortLabel || 'step'}
                    </Button>
                  ) : null}
                  {onRetryAndContinue ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 rounded-sm border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                      disabled={resumeLoading}
                      onClick={onRetryAndContinue}
                    >
                      Retry &amp; continue
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {pipelineStatus === 'ready' && selectedStep.id === 'done' && onOpenEditor ? (
              <div className="px-4 py-3 border-t border-slate-800 shrink-0">
                <Button
                  type="button"
                  className="w-full sm:w-auto bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-sm"
                  onClick={onOpenEditor}
                >
                  Open tutorial editor
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        {viewerTabs.length > 0 && selectedStep.kind === 'orchestration' ? (
          <p className="text-[10px] text-slate-600 px-5 py-2 border-t border-slate-800 shrink-0">
            Raw orchestration tabs also available via “View full JSON” on the flow page.
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
